import { useEffect, useMemo, useState } from 'react';
import { listExports, reExport } from '../../api/originatorApi';
import { formatDate } from '../../utils/format';
import { DataTable } from '../ui/DataTable';
import { StatusPill } from '../ui/StatusPill';
import { Pagination } from '../ui/Pagination';
import { Button } from '../ui/Button';
import { ErrorBanner } from '../ui/ErrorBanner';

const PAGE_SIZE = 20;

/** O10E — export history with a per-row re-export action. */
export function ExportHistorySection() {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [reexportingId, setReexportingId] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listExports({ page, pageSize: PAGE_SIZE });
        if (cancelled) return;
        setRows(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load export history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, reloadToken]);

  async function handleReExport(exportId) {
    setReexportingId(exportId);
    setNotice('');
    try {
      await reExport(exportId);
      setNotice('Re-export job submitted.');
      setReloadToken((t) => t + 1);
    } catch (requestError) {
      setError(requestError.message || 'Unable to re-export.');
    } finally {
      setReexportingId(null);
    }
  }

  const columns = useMemo(
    () => [
      {
        key: 'id',
        header: 'Export',
        render: (row) => (
          <span className="font-mono text-[11px] font-semibold text-eorig-ink">
            {row.id}
          </span>
        ),
      },
      { key: 'export_type', header: 'Type' },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusPill value={row.status} />,
      },
      {
        key: 'error_message',
        header: 'Error',
        render: (row) => (
          <span className="text-rose-600">{row.error_message || '—'}</span>
        ),
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (row) => (
          <span className="text-eorig-muted">{formatDate(row.created_at)}</span>
        ),
      },
      {
        key: 'updated_at',
        header: 'Updated',
        render: (row) => (
          <span className="text-eorig-muted">{formatDate(row.updated_at)}</span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'right',
        render: (row) => (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            disabled={reexportingId === row.id}
            onClick={() => handleReExport(row.id)}
          >
            {reexportingId === row.id ? 'Re-exporting…' : 'Re-export'}
          </Button>
        ),
      },
    ],
    [reexportingId]
  );

  return (
    <div className="space-y-3 p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-eorig-ink">Export history</h3>
        {notice ? (
          <span className="text-xs font-medium text-emerald-600">{notice}</span>
        ) : null}
      </div>

      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No exports yet."
        minWidth={760}
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        disabled={loading}
      />
    </div>
  );
}
